import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  usePayDataBankLookupQuery,
  usePayDataTruckIdLookupQuery,
  usePtbfPreviewMutation,
  usePtbfSaveMutation,
  type PtbfRowInput,
} from '@/store/api/procurementApi'
import { useLazySuggestXnttNoQuery } from '@/store/api/procurementApi'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, numSx, roSx } from '../purchase/dialogStyle'
import MassInputGrid, {
  type MassColumn,
  type MassRow,
  type MassVerdict,
} from './MassInputGrid'
import PreviewDataGrid, { type PreviewColumn } from './PreviewDataGrid'

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring (SPEC C3).
// Guard (contract_type <> 'Outright', P-*, not forced close) is done by the
// caller before opening — the dialog does not re-check it.
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  contractNo: string
  /** Raw text of the parent screen's "Requested FX" box; parsed here, BE falls back to fixed FX. */
  requestedFx: string
  /** Parent screen's fix level — pre-fills the editable "Futures (Lastest)" field. */
  fixLevel: number | null
  onClose: () => void
  onSaved: () => void
}

/** Cột input grid — thứ tự + danh sách khớp Access table `public_tmp_addmany_pay_ptbf`
 *  ([FIELDS] 12 cột: Payment Type, Truck ID, Bank Name, % Pay, Quantity KG, Advanced USD,
 *  VAT Amount VND, Description, Sub Price, Advanced sub USD, Request sub USD, Request sub VND).
 *  Price/Futures/Requested FX/Price Unit KHÔNG có trong table Access → BE tự tính từ
 *  contract + Futures(Lastest) + context (row gửi null → compute). Combobox: Payment Type
 *  (value list), Truck ID (goods_allocation theo contract), Bank Name (cp_bank), % Pay
 *  (freeSolo suggest 100/80/75/90/20 %). */
const PCT_PAY_SUGGEST = ['100%', '80%', '75%', '90%', '20%']
const PTBF_COLUMNS: MassColumn[] = [
  {
    key: 'payment_type',
    label: 'Payment Type',
    kind: 'combo',
    width: 140,
    required: true,
    options: [
      { value: 'Provisional', label: 'Provisional' },
      { value: 'Fixation', label: 'Fixation' },
    ],
  },
  { key: 'truck_id', label: 'Truck ID', kind: 'combo', width: 120 },
  { key: 'bank_name', label: 'Bank Name', kind: 'combo', width: 150 },
  { key: 'pct_pay', label: '% Pay', kind: 'number', width: 90, suggest: PCT_PAY_SUGGEST },
  { key: 'quantity_kg', label: 'Quantity KG', kind: 'number', width: 110, sum: true },
  { key: 'advanced_usd', label: 'Advanced USD', kind: 'number', width: 120, sum: true },
  { key: 'vat_amount_vnd', label: 'VAT Amount VND', kind: 'number', width: 130, sum: true },
  { key: 'description', label: 'Description', kind: 'text', width: 160 },
  { key: 'sub_price', label: 'Sub Price', kind: 'number', width: 100 },
  { key: 'advanced_sub_usd', label: 'Advanced sub USD', kind: 'number', width: 130, sum: true },
  { key: 'request_sub_usd', label: 'Request sub USD', kind: 'number', width: 130, sum: true },
  { key: 'request_sub_vnd', label: 'Request sub VND', kind: 'number', width: 130, sum: true },
]

let ROW_SEQ = 0
const makeEmptyRow = (): MassRow => {
  const data: Record<string, string> = {}
  PTBF_COLUMNS.forEach((c) => {
    data[c.key] = ''
  })
  return { id: ++ROW_SEQ, data }
}

/** Dòng có dữ liệu? — bất kỳ ô nào non-empty (mirror Access isRowEmpty all-fields). */
const isRowFilled = (d: Record<string, string>): boolean =>
  PTBF_COLUMNS.some((c) => (d[c.key] ?? '').trim() !== '')

/** Parse "1,234.5" → number | null. */
const parseNum = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Parse % Pay — bỏ ký tự "%" (suggest "80%") rồi ra số (80). */
const parsePct = (s: string): number | null => parseNum((s ?? '').replace(/%/g, ''))

/** Format numeric — thousands sep, up to `max` decimals. */
const fmtNum = (value: number | null | undefined, max = 2): string => {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: max })
}

/** Access Percent format: 0.7 → "70.00%". */
const fmtPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return `${(value * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

/**
 * "% Pay" UI ↔ API conversion: the user types a percent NUMBER (50 = 50%),
 * consistent with the sibling Outright dialog, but the BE compute chain
 * (Access `[% Pay]`, `PtbfInputRow.pct_pay`) expects a FRACTION (1 = 100%,
 * 0.5 = 50%) — divide by 100 before sending to /ptbf/preview and /ptbf/save.
 */
const pctToFraction = (pct: number | null): number | null => (pct === null ? null : pct / 100)

const toInputRow = (d: Record<string, string>): PtbfRowInput => ({
  payment_type: (d.payment_type ?? '').trim() || null,
  truck_id: (d.truck_id ?? '').trim() || null,
  bank_name: (d.bank_name ?? '').trim() || null,
  quantity_kg: parseNum(d.quantity_kg),
  pct_pay: pctToFraction(parsePct(d.pct_pay)),
  advanced_usd: parseNum(d.advanced_usd),
  price: parseNum(d.price),
  futures: parseNum(d.futures),
  requested_fx: parseNum(d.requested_fx),
  price_unit: (d.price_unit ?? '').trim() || null,
  sub_price: parseNum(d.sub_price),
  advanced_sub_usd: parseNum(d.advanced_sub_usd),
  request_sub_usd: parseNum(d.request_sub_usd),
  request_sub_vnd: parseNum(d.request_sub_vnd),
  description: (d.description ?? '').trim() || null,
  vat_amount_vnd: parseNum(d.vat_amount_vnd),
})

// Cross-check per-row — rule client-checkable (mirror BE _cdp_validate_rows):
// payment_type PHẢI là 'Provisional' | 'Fixation'. Truck/Bank/Fixation-fully-fixed
// cần DB → BE bắt lúc Save (báo snack).
function validateRow(d: Record<string, string>): MassVerdict {
  const badFields = new Set<string>()
  const messages: string[] = []
  if (!isRowFilled(d)) return { ok: true, badFields, messages }
  const pt = (d.payment_type ?? '').trim()
  if (pt !== 'Provisional' && pt !== 'Fixation') {
    badFields.add('payment_type')
    messages.push("Please type 'Provisional' or 'Fixation' !")
  }
  return { ok: badFields.size === 0, badFields, messages }
}

// Bảng preview "Check data" — theo template /convert-table (freeze Contract No).
// Thứ tự + format (decimals) khớp query Access ProcurementPay_WritePTBF.
const num = (max: number) => (v: unknown) => fmtNum(v as number | null | undefined, max)
const PTBF_PREVIEW_COLUMNS: PreviewColumn[] = [
  { key: 'date', label: 'Date', kind: 'date', width: 110 },
  { key: 'contract_no', label: 'Contract No', kind: 'text', width: 130, freeze: true },
  { key: 'payment_type', label: 'Payment Type', kind: 'text', width: 110 },
  { key: 'truck_id', label: 'Truck ID', kind: 'text', width: 90 },
  { key: 'bank_name', label: 'Bank Name', kind: 'text', width: 130 },
  { key: 'to_be_requested', label: 'To be Requested', kind: 'number', width: 120, align: 'right', sum: true },
  { key: 'quantity_kg', label: 'Quantity (KG)', kind: 'number', width: 110, align: 'right', sum: true },
  { key: 'price', label: 'Price', kind: 'number', width: 100, align: 'right' },
  { key: 'futures', label: 'Futures', kind: 'number', width: 100, align: 'right' },
  { key: 'price_unit', label: 'Price Unit', kind: 'text', width: 90 },
  { key: 'requested_fx', label: 'Requested FX', kind: 'number', width: 110, align: 'right' },
  { key: 'advanced_usd', label: 'Advanced (USD)', kind: 'number', width: 120, align: 'right', format: num(3), sum: true },
  { key: 'request_usd', label: 'Request (USD)', kind: 'number', width: 120, align: 'right', format: num(3), sum: true },
  { key: 'request_vnd', label: 'Request (VND)', kind: 'number', width: 120, align: 'right', format: num(0), sum: true },
  { key: 'vat_amount_vnd', label: 'VAT Amount (VND)', kind: 'number', width: 130, align: 'right', format: num(0), sum: true },
  { key: 'description', label: 'Description', kind: 'text', width: 160 },
  { key: 'sub_price', label: 'Sub Price', kind: 'number', width: 100, align: 'right' },
  { key: 'request_sub_usd', label: 'Request sub (USD)', kind: 'number', width: 140, align: 'right', format: num(3), sum: true },
  { key: 'request_sub_vnd', label: 'Request sub (VND)', kind: 'number', width: 140, align: 'right', sum: true },
  { key: 'advanced_sub_usd', label: 'Advanced sub (USD)', kind: 'number', width: 140, align: 'right', format: num(3), sum: true },
]

/**
 * Contract Detail → "Add Pay PTBF" batch dialog — converts Access
 * `frmProcurementQryDetailsAddPayPTBF` ("Procurement - Pay (PTBF)").
 *
 * Top: read-only contract context + editable XNTT No + editable "Futures
 * (Lastest)". Middle: Excel-like input grid (style Goods Allocation › Mass
 * Upload — dán từ Excel / checkbox / Copy·Delete·Reset / cột status ✓✗).
 * Bottom: "Refresh to Check" preview grid mirroring Access
 * `ProcurementPay_WritePTBF` (BE-computed); Save inserts every line.
 */
export default function ContractDetailAddPayPTBFDialog({
  open,
  contractNo,
  requestedFx,
  fixLevel,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  const [runPreview, { data: preview, isLoading: isPreviewing }] = usePtbfPreviewMutation()
  const [runSave, { isLoading: isSaving }] = usePtbfSaveMutation()
  const [suggestXntt] = useLazySuggestXnttNoQuery()

  // Combobox lookups (Access table Lookup): Truck ID theo contract, Bank list,
  // Price Unit (public_price_unit).
  const { data: truckData } = usePayDataTruckIdLookupQuery(
    { contractNo },
    { skip: !open || !contractNo },
  )
  const { data: bankData } = usePayDataBankLookupQuery(undefined, { skip: !open })

  const columns = useMemo<MassColumn[]>(
    () =>
      PTBF_COLUMNS.map((c) => {
        if (c.key === 'truck_id') return { ...c, options: truckData?.items ?? [] }
        if (c.key === 'bank_name') return { ...c, options: bankData?.items ?? [] }
        return c
      }),
    [truckData, bankData],
  )

  const [rows, setRows] = useState<MassRow[]>([makeEmptyRow()])
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [xnttNo, setXnttNo] = useState('')
  const [futures, setFutures] = useState('')
  // Gate spinner riêng cho cụm context read-only (preview lần đầu) — body/grid nhập render ngay.
  const [contextLoading, setContextLoading] = useState(false)

  const parsedFx = useMemo(() => parseNum(requestedFx), [requestedFx])
  const parsedFutures = useMemo(() => parseNum(futures), [futures])

  const verdicts = useMemo(() => rows.map((r) => validateRow(r.data)), [rows])
  const filledCount = useMemo(() => rows.filter((r) => isRowFilled(r.data)).length, [rows])
  const allValid = filledCount > 0 && verdicts.every((v) => v.ok)

  // Re-init each time the dialog opens: reset grid, prefill Futures (Lastest)
  // from the parent's fix level, fetch header context (preview with rows=[])
  // + suggest XNTT No.
  useEffect(() => {
    if (!open || !contractNo) return
    setRows([makeEmptyRow()])
    setSelected(new Set())
    setXnttNo('')
    setFutures(fixLevel !== null && fixLevel !== undefined ? String(fixLevel) : '')
    setContextLoading(true)
    skipAutoPreviewRef.current = true // open đã tự preview rows=[] → bỏ lần auto đầu.
    void (async () => {
      try {
        await runPreview({
          contract_no: contractNo,
          requested_fx: parseNum(requestedFx),
          futures: fixLevel,
          rows: [],
        }).unwrap()
      } catch {
        // preview is best-effort — context stays blank on error
      } finally {
        setContextLoading(false)
      }
    })()
    void (async () => {
      try {
        const sug = await suggestXntt({ contractNo }).unwrap()
        setXnttNo(sug.suggestedNo)
      } catch {
        // suggest is best-effort — user can still type the XNTT No manually
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractNo])

  const payloadRows = (): PtbfRowInput[] =>
    rows.filter((r) => isRowFilled(r.data)).map((r) => toInputRow(r.data))

  // Signature payload input → trigger auto-refresh preview khi Input data đổi.
  const rowsSignature = useMemo(
    () => JSON.stringify(rows.filter((r) => isRowFilled(r.data)).map((r) => toInputRow(r.data))),
    [rows],
  )
  const skipAutoPreviewRef = useRef(false)
  const previewReqRef = useRef<{ abort?: () => void } | null>(null)

  // Bắn preview ngay (abort request đang chạy → tối đa 1 in-flight). Dùng cho cả
  // auto-refresh (debounce) lẫn nút Refresh thủ công.
  const firePreview = () => {
    previewReqRef.current?.abort?.()
    const req = runPreview({
      contract_no: contractNo,
      requested_fx: parsedFx,
      futures: parsedFutures,
      rows: payloadRows(),
    })
    previewReqRef.current = req
    void req.unwrap().catch(() => {
      /* preview best-effort (kể cả abort) — lỗi thật sẽ hiện lại khi Save */
    })
  }

  // Auto-refresh preview (debounce 2s) khi Input data / Requested FX / Futures
  // đổi. Nút Refresh (trên hàng Filter của preview) để cập nhật ngay.
  useEffect(() => {
    if (!open || !contractNo) return
    if (skipAutoPreviewRef.current) {
      skipAutoPreviewRef.current = false
      return
    }
    const handle = setTimeout(firePreview, 2000)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractNo, parsedFx, parsedFutures, rowsSignature])

  const handleSave = async () => {
    if (filledCount === 0) {
      snack.warning('Please input at least one payment row !')
      return
    }
    const firstBad = verdicts.findIndex((v) => !v.ok)
    if (firstBad >= 0) {
      snack.warning(`Row ${firstBad + 1}: ${verdicts[firstBad].messages[0]}`)
      return
    }
    try {
      const res = await runSave({
        contract_no: contractNo,
        requested_fx: parsedFx,
        futures: parsedFutures,
        rows: payloadRows(),
        xntt_no: xnttNo.trim() || null,
      }).unwrap()
      snack.success(`Raw records: ${res.raw_records} — Error: ${res.errors}`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save'))
    }
  }

  const roField = (label: string, value: string, numeric = false) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={numeric ? [numSx, roSx] : roSx}
    />
  )

  const ctx = preview?.context
  const previewRows = preview?.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, px: 2, display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: '1rem' }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
          <Box component="span" title="Procurement - Pay (PTBF)">
            Inbound Logistics - Pay (PTBF)
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => void handleSave()}
              disabled={isSaving || isPreviewing || !allValid}
              sx={dialogHeaderBtnSx}
            >
              {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" onClick={onClose} disabled={isSaving} sx={dialogHeaderBtnSx}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ----- Contract context (label left / value right, 2 columns) ----- */}
        {contextLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2, mb: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
              mb: 1.5,
            }}
          >
            <Box>{roField('Contract No', contractNo)}</Box>
            <Box>
              <TextField
                label="XNTT No"
                size="small"
                fullWidth
                value={xnttNo}
                onChange={(e) => setXnttNo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isSaving}
              />
            </Box>
            <Box>{roField('Date', dayjs().format('DD-MMM-YYYY'))}</Box>
            {/* Futures (Lastest) — readonly, đúng rule Access:
                DLookup("last","v_pos_dashboard_pnlmarket","symbol=futures_month") = header fix_level
                (prop fixLevel). Format Standard. */}
            <Box>{roField('Futures (Lastest)', fmtNum(fixLevel), true)}</Box>

            <Box>{roField('Fixed FX (on Contract)', fmtNum(ctx?.fixed_fx), true)}</Box>
            <Box>{roField('Provi. on Market Price', fmtPercent(ctx?.provi_market_rate), true)}</Box>
            <Box>{roField('Requested FX', fmtNum(ctx?.requested_fx), true)}</Box>
            <Box>{roField('Applicable VAT', ctx?.vat_txt ?? '')}</Box>

            <Box>{roField('To be Receive', fmtNum(ctx?.pending), true)}</Box>
            <Box>{roField('Pending.Provisional', fmtNum(ctx?.pending_provisional), true)}</Box>
            <Box>{roField('Pending.Fixation', fmtNum(ctx?.pending_fixation), true)}</Box>
          </Box>
        )}

        {/* ----- Input grid (Access subForm: tmp_addmany_pay_ptbf) ----- */}
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Input data</Typography>
          <Typography sx={{ color: 'error.main', fontStyle: 'italic', fontSize: '0.7rem' }}>
            * Advanced auto-filled (Contract + TruckID + Quantity) when &quot;Advanced USD&quot; is blank
          </Typography>
        </Stack>
        <MassInputGrid
          open={open}
          columns={columns}
          rows={rows}
          setRows={setRows}
          selected={selected}
          setSelected={setSelected}
          makeEmptyRow={makeEmptyRow}
          isRowFilled={isRowFilled}
          verdicts={verdicts}
          filledCount={filledCount}
          disabled={isSaving}
          emptyStateText="Enter at least one payment row to check"
          snackbar={snack}
          rowNoun="row"
          showCopy={false}
        />

        {/* ----- Check section: title + New pendings gộp vào hàng Filter của preview ----- */}
        <Divider sx={{ my: 1.5 }} />
        <PreviewDataGrid
          columns={PTBF_PREVIEW_COLUMNS}
          rows={previewRows as unknown as Record<string, unknown>[]}
          emptyText={isPreviewing ? 'Loading…' : 'No preview rows — input data above.'}
          height={300}
          title="Check data (auto-updated)"
          loading={isPreviewing}
          onRefresh={firePreview}
          rightSlot={
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Box sx={{ width: { xs: '100%', sm: 190 } }}>
                {roField('New pending.Provisional', fmtNum(preview?.new_pending_provisional), true)}
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 190 } }}>
                {roField('New pending.Fixation', fmtNum(preview?.new_pending_fixation), true)}
              </Box>
            </Stack>
          }
        />
      </DialogContent>
    </Dialog>
  )
}
